import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Global axios defaults for cookie auth
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

// Standalone-Build (Electron .exe): kein Backend — statischer Presenter-User,
// alle Folien sichtbar, keine Agenda.
const STANDALONE = process.env.REACT_APP_STANDALONE === 'true';
const OFFLINE_USER = {
  id: 'offline-presenter',
  email: 'offline@stig-rock.local',
  name: 'STIG Rock',
  role: 'presenter',
  agenda: [],
  section_visibility: {},
};

export function formatApiError(detail) {
  if (detail == null) return 'Etwas ist schiefgelaufen. Bitte erneut versuchen.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = guest, obj = user
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (STANDALONE) {
      setUser(OFFLINE_USER);
      setLoading(false);
      return OFFLINE_USER;
    }
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
      return data;
    } catch (e) {
      // No session → user must log in. Auto-guest-login entfernt (08/2026).
      // "Freier Zugang" nur noch über die öffentliche /pdf-Route.
      setUser(false);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      /* ignore */
    }
    setUser(false);
  }, []);

  const saveConfig = useCallback(async (agenda, section_visibility) => {
    const { data } = await axios.put(`${API}/config`, { agenda, section_visibility });
    setUser(data);
    return data;
  }, []);

  const value = { user, loading, login, logout, refresh, saveConfig, API };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
